import React from 'react';
import { Drawer, Box, Typography, Button, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { LoadingIndicator, MoreActionsMenuButton, OccupyFreeSpace, useWindowHotkey } from '../primitives';
import { FormControlsForFields, TableForFields } from '../schemed';
import { Schema } from '../../hooks/useSchema';
import { ExtraProps } from '../schemed/FormControlProps';
import { FormattedMessage } from 'react-intl';
import { AttributesWrapper, ContactNameWithNotices } from './Elements/Primitives';
import { OrganizationsListData } from './data/useOrganizationsList';
import { DescribedAddressLinks } from './Elements/DescribedAddressList';
import { ContactSchema } from './types';

export interface OrganizationEditDrawerProps {
  schema: Schema;
  editRecord: OrganizationsListData["editRecord"];
  removeRecord: OrganizationsListData["remove"]["run"];
}

export const OrganizationEditDrawer = ({ schema, editRecord, removeRecord }: OrganizationEditDrawerProps) => {
  const isDrawerOpen = !!editRecord?.item;

  const getTitle = () => {
    const r = editRecord?.item;
    if (!r) return "Редактирование";
    return r.name || "Новая компания";
  };

  const label = (f: string) => (
    <Typography key={`${f}_label`} variant="caption" color="textSecondary" className="attribute-label">
      {schema[f]?.label}
    </Typography>
  );

  const control = (f: string, options?: ExtraProps) => (
    <FormControlsForFields
      key={`${f}_control`}
      fields={[
        [f, { label: " ", ...(options || {})}],
      ]}
      schema={schema}
      data={editRecord.item}
      onChange={(o, c) => editRecord.update(c)}
    />
  );

  useWindowHotkey("esc", () => editRecord.cancel());
  useWindowHotkey("alt+s", () => editRecord.save());

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={isDrawerOpen}
      elevation={8}
      PaperProps={{
        sx: { 
          width: 600, 
          p: 2,
        }
      }}
    >
      {isDrawerOpen && (
        <Box display="flex" flexDirection="column" height="100%">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{getTitle()}</Typography>
            
            <OccupyFreeSpace />

            <MoreActionsMenuButton
              actions={[
                ["remove", <FormattedMessage id="common.delete" />, () => editRecord.item && removeRecord(editRecord.item)]
              ]}
              />
            <IconButton size="small" onClick={() => editRecord.cancel()}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <AttributesWrapper columns="1fr 2fr">
            
            {label("name")}
            {control("name", { controlProps: { autoFocus: true }})}

            {label("description")}
            {control("description", { controlProps: { multiline: true, rows: 4 }})}

            {/* {label("owner_id")}
            {control("owner_id")} */}
            
          </AttributesWrapper>

          <Box gap={1}>
            {editRecord.contacts.isLoading && <LoadingIndicator size="s" />}

            {!editRecord.contacts.isLoading &&
              <TableForFields
                data={editRecord.contacts.data}
                fields={[
                  ["name", { label: " "}],
                  ["contacts", { label: " "}],
                ]}
                fieldElement={f => {
                  switch(f) {
                    case "name":
                      return r => (
                        <Box display="flex" flexDirection="column" gap={0}>
                          <ContactNameWithNotices contact={r} />
                          {!!r.organization_position &&
                            <Typography variant="caption" color="textSecondary">{r.organization_position}</Typography>}

                          {(!!r.geo_country?.length || !!r.geo_city?.length) &&
                            <Typography variant="caption" color="textSecondary">{[...(r.geo_country || []), ...(r.geo_city || [])].join(", ")}</Typography>}
                        </Box>);
                    case "contacts":
                      return r => <Box display="flex" flexDirection="column" gap={0.5}>
                        <DescribedAddressLinks noSeparators items={r.emails} />
                        <DescribedAddressLinks noSeparators items={r.phones} />
                      </Box>
                  }
                }}
                onRowClick={r => {
                  const url = `/org/crm/contact?id=${r._id}`;
                  window.open(url, '_blank', 'noopener,noreferrer');
                }}
                rowStyle={r => ({
                  verticalAlign: "top",
                  opacity: r.organization_is_active ? 1 : 0.5,
                })}
                schema={ContactSchema}
              />}
          </Box>

          <Box mt="auto" pt={2} display="flex" justifyContent="flex-end" gap={1}>
            <Button size="small" onClick={() => editRecord.cancel()}>
              <FormattedMessage id="common.close" />
            </Button>
            {editRecord.hasChanges &&
              <Button variant="contained" color="primary" onClick={() => editRecord.save()}>
                <FormattedMessage id="common.save" />
              </Button>}
          </Box>
        </Box>
      )}
    </Drawer>
  );
};